import { AppContext } from 'App.context';
import React, { useContext, useMemo } from 'react';
import useDisableLicenceManagerHook from './DisableLicenceManager.hook';
import { getLicenceIconUrl } from '../utils';
import HotelSelector from 'Components/common/HotelSelector/HotelSelector';
import { Table, Space, Button, Typography, Tag } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import constants from 'utils/constants';
import { useShowNoPermissionNotification } from 'Components/common/Notifications/showNoPermissionNotification';

const { Text } = Typography;

export default function DisableLicenceManager() {
  const {
    appActions: { translate },
    appState,
  } = useContext(AppContext);
  const { state, actions } = useDisableLicenceManagerHook();
  const isAdmin = appState.role === 'admin' || appState.userType === 'admin';
  const showNoPermission = useShowNoPermissionNotification();

  const columns = useMemo(() => {
    const base = [
      {
        title: translate('accountDetail.disableLicence.licence'),
        key: 'licence',
        render: (_, licence) => {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img
                src={getLicenceIconUrl(licence?.name)}
                alt={licence?.name}
                style={{
                  width: 32,
                  height: 32,
                  objectFit: 'contain',
                  borderRadius: 4,
                  background: '#f5f5f5',
                }}
              />

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 'bold' }}>
                  {translate('billingConfig.licence.types.' + licence.name)}
                </span>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>L{licence?.id}</span>
              </div>
            </div>
          );
        },
      },
      {
        title: translate('accountDetail.disableLicence.reasons'),
        dataIndex: 'reasons',
        key: 'reasons',

        render: (_, licence) => {
          const reasonCodes = String(licence?.endReason || '')
            .split(';')
            .map(s => s.trim())
            .filter(Boolean);

          const translated = reasonCodes.map(code => {
            return translate(`accountDetail.disableLicence.reasonStep.reasons.${code}`);
          });

          const other = licence?.endReasonOther?.trim();
          if (other) translated.push(other);

          const hasOther = Boolean(other);
          const codeLabels = hasOther ? translated.slice(0, -1) : translated;
          const otherText = hasOther ? translated[translated.length - 1] : null;

          const colorMap = {
            [constants.LICENCE_END_REASONS.NOTNEEDED]: 'green',
            [constants.LICENCE_END_REASONS.HIGHCOST]: 'volcano',
            [constants.LICENCE_END_REASONS.MISSINGFEATURES]: 'gold',
            [constants.LICENCE_END_REASONS.TECHNICALISSUES]: 'orange',
            [constants.LICENCE_END_REASONS.POORSUPPORT]: 'magenta',
            [constants.LICENCE_END_REASONS.LOWADOPTION]: 'blue',
            [constants.LICENCE_END_REASONS.SWITCHEDPROVIDER]: 'cyan',
            [constants.LICENCE_END_REASONS.TOOCOMPLEX]: 'geekblue',
            [constants.LICENCE_END_REASONS.OTHER]: 'purple',
          };

          if (!translated.length) return <div>—</div>;

          return (
            <div>
              <Space size={[4, 4]} wrap>
                {codeLabels.map((label, idx) => {
                  const code = reasonCodes[idx];
                  const color = colorMap[code] || 'default';
                  return (
                    <Tag key={`reason-${licence?.id}-${idx}`} color={color}>
                      {label}
                    </Tag>
                  );
                })}
              </Space>

              {hasOther && (
                <Typography.Paragraph
                  style={{ marginTop: 6, marginBottom: 0, wordBreak: 'break-word' }}
                  ellipsis={{
                    rows: 2,
                    expandable: true,
                    symbol: translate('accountDetail.disableLicence.viewMore'),
                  }}
                >
                  {otherText}
                </Typography.Paragraph>
              )}
            </div>
          );
        },
      },
      {
        title: translate('accountDetail.disableLicence.renewalDate'),
        dataIndex: 'renewalDate',
        key: 'renewalDate',
        render: (_, licence) => {
          return licence.renewalDate
            ? new Date(licence.renewalDate).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : '-';
        },
      },
      {
        title: translate('accountDetail.disableLicence.endDate'),
        dataIndex: 'endDate',
        key: 'endDate',
        render: (_, licence) => {
          return licence.endDate
            ? new Date(licence.endDate).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : '-';
        },
      },
      {
        title: translate('accountDetail.disableLicence.endRequestDate'),
        dataIndex: 'endRequestDate',
        key: 'endRequestDate',
        render: date => {
          if (!date) return '—';
          return new Date(date).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        },
      },
      {
        title: translate('accountDetail.disableLicence.actions'),
        key: 'actions',
        render: (_, licence) => {
          if (appState.superUser) {
            return (
              <Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => actions.handleAccept(licence)}
                >
                  {translate('accountDetail.disableLicence.accept')}
                </Button>

                <Button
                  danger
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => actions.handleDeny(licence)}
                >
                  {translate('accountDetail.disableLicence.deny')}
                </Button>
              </Space>
            );
          }

          if (licence?.endStatus === constants.LICENCE_END_STATUS.REQUESTED) {
            return (
              <Button
                danger
                size="small"
                onClick={() => {
                  if (isAdmin) {
                    showNoPermission('account_detail', 'try_cancel_licence_end', {});
                  } else {
                    actions.handleDeny(licence);
                  }
                }}
              >
                {translate('accountDetail.disableLicence.cancel')}
              </Button>
            );
          }

          if (licence?.endStatus === constants.LICENCE_END_STATUS.APPROVED) {
            return (
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  if (isAdmin) {
                    showNoPermission('account_detail', 'request_reactivation', {});
                  } else {
                    actions.handleRequestReactivation(licence);
                  }
                }}
              >
                {translate('accountDetail.disableLicence.requestReactivation')}
              </Button>
            );
          }

          return null;
        },
      },
    ];

    if (state.isAllHotelsSelected) {
      base.splice(1, 0, {
        title: translate('accountDetail.disableLicence.hotel'),
        key: 'hotel',
        render: (_, licence) => {
          const hotelId = licence?.hotelId || licence?.hotel?.id || licence?.hotelKey || '';
          const hotelName =
            licence?.hotelName ||
            licence?.hotel?.name ||
            (appState?.hotelList || []).find(h => String(h.id) === String(hotelId))?.name ||
            '';

          if (appState.superUser) return `${hotelId} - ${hotelName}`;
          else return hotelName || '-';
        },
      });
    }

    if (appState.superUser === false) {
      const endStatusColumn = {
        title: translate('accountDetail.disableLicence.licenceEndStatusTitle'),
        dataIndex: 'endStatus',
        key: 'endStatus',
        render: (_, licence) => {
          const status = licence?.endStatus;
          if (!status) return '—';
          let tagColor = 'default';
          if (licence.endStatus === constants.LICENCE_END_STATUS.REQUESTED) tagColor = 'processing';
          if (licence.endStatus === constants.LICENCE_END_STATUS.APPROVED) tagColor = 'success';
          if (licence.endStatus === constants.LICENCE_END_STATUS.DENIED) tagColor = 'error';

          return (
            <Tag color={tagColor}>
              {translate(`accountDetail.disableLicence.licenceEndStatus.${status}`)}
            </Tag>
          );
        },
      };

      const insertBeforeIndex = base.findIndex(col => col.key === 'renewalDate');
      base.splice(insertBeforeIndex === -1 ? base.length : insertBeforeIndex, 0, endStatusColumn);
    }

    return base;
  }, [translate, state.isAllHotelsSelected, state.loadedLicences]);

  return (
    <div>
      <HotelSelector
        enableAllHotels={true}
        darkTheme={true}
        onHotelSelected={actions.handleHotelSelected}
      />

      <div style={{ marginTop: 16 }}>
        <Table
          rowKey="id"
          dataSource={state.loadedLicences || []}
          columns={columns}
          pagination={false}
          loading={state.isLicencesLoading}
          locale={{ emptyText: translate('accountDetail.disableLicence.noPending') }}
        />
      </div>
    </div>
  );
}
